import {
  DataRow,
  TableRowClickHandlerInternal,
  TableRowSelectHandlerInternal,
  SelectionChangeHandler,
  TableSelectionModel,
} from "@vuu-ui/vuu-table-types";
import {
  deselectItem,
  dispatchMouseEvent,
  queryClosest,
  selectItem,
  splitSelectableRanges,
} from "@vuu-ui/vuu-utils";
import {
  KeyboardEvent,
  KeyboardEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getRowElementByAriaIndex } from "./table-dom-utils";
import { TableProps } from "./Table";
import { DataSource, RowSelectionEventHandler } from "@vuu-ui/vuu-data-types";

const orderedRowKeys = (
  activeRowIdentifier: RowIdentifier | undefined,
  newRowIdentifier: RowIdentifier,
  rangeSelect = false,
): [string, string] | [string] => {
  if (rangeSelect && activeRowIdentifier) {
    if (newRowIdentifier.rowIdx > activeRowIdentifier.rowIdx) {
      return [activeRowIdentifier.rowKey, newRowIdentifier.rowKey];
    } else {
      return [newRowIdentifier.rowKey, activeRowIdentifier.rowKey];
    }
  } else {
    return [newRowIdentifier.rowKey];
  }
};

const defaultSelectionKeys = ["Enter", " "];

type RowIdentifier = {
  rowIdx: number;
  rowKey: string;
};

export interface SelectionHookProps
  extends Pick<TableProps, "allowSelectCheckboxRow" | "onSelectionChange"> {
  containerRef: RefObject<HTMLElement | null>;
  dataSource: DataSource;
  dataRowsRef: RefObject<DataRow[]>;
  highlightedIndexRef: RefObject<number | undefined>;
  /**
   * When provided, rows for which this returns false will not be selectable.
   */
  isRowSelectable?: (dataRow: DataRow) => boolean;
  selectionKeys?: string[];
  selectionModel: TableSelectionModel;
  onSelectionChange: SelectionChangeHandler;
  onSelect?: TableRowSelectHandlerInternal;
}

export const useSelection = ({
  allowSelectCheckboxRow,
  containerRef,
  dataSource,
  dataRowsRef,
  highlightedIndexRef,
  isRowSelectable,
  onSelect,
  onSelectionChange,
  selectionKeys = defaultSelectionKeys,
  selectionModel,
}: SelectionHookProps) => {
  const lastActiveRef = useRef<RowIdentifier | undefined>(undefined);
  const [allRowsSelected, setAllRowsSelected] = useState(false);

  const selectionAllowed =
    selectionModel !== "none" && selectionModel !== "checkbox-disabled";

  const handleRowSelection = useCallback<RowSelectionEventHandler>(
    (selectedRowsCount) => {
      setAllRowsSelected((allSelected) =>
        allSelected && selectedRowsCount < dataSource.size
          ? false
          : allSelected,
      );
    },
    [dataSource.size],
  );

  useEffect(() => {
    dataSource.on("row-selection", handleRowSelection);
    return () => dataSource.removeListener("row-selection", handleRowSelection);
  }, [dataSource, handleRowSelection]);

  const isSelectionEvent = useCallback(
    (evt: KeyboardEvent<HTMLElement>) => selectionKeys.includes(evt.key),
    [selectionKeys],
  );

  const handleRowClick = useCallback<TableRowClickHandlerInternal>(
    (e, dataRow, rangeSelect, keepExistingSelection) => {
      if (isRowSelectable && !isRowSelectable(dataRow)) {
        return;
      }
      const { index: rowIdx, key: rowKey } = dataRow;
      const { current: activeRowKey } = lastActiveRef;
      const newRowIdentifier = { rowIdx, rowKey } as RowIdentifier;

      if (dataRow.isSelected && selectionModel === "single-no-deselect") {
        return;
      }

      const selectOperation = dataRow.isSelected ? deselectItem : selectItem;

      if (selectionModel === "checkbox" && allowSelectCheckboxRow !== true) {
        const cell = queryClosest(e.target, ".vuuTableCell");
        if (!cell?.querySelector(".vuuCheckboxRowSelector")) {
          return;
        }
      }

      const [fromRowKey, toRowKey] = orderedRowKeys(
        activeRowKey,
        newRowIdentifier,
        rangeSelect,
      );
      if (rangeSelect && isRowSelectable && activeRowKey && toRowKey) {
        // Split the range into sub-ranges, skipping non-selectable rows
        const minIdx = Math.min(rowIdx, activeRowKey.rowIdx);
        const maxIdx = Math.max(rowIdx, activeRowKey.rowIdx);
        const rowsInRange = dataRowsRef.current
          .filter((r) => r.index >= minIdx && r.index <= maxIdx)
          .sort((a, b) => a.index - b.index);
        const requests = splitSelectableRanges(
          rowsInRange,
          isRowSelectable,
          keepExistingSelection,
        );
        lastActiveRef.current = newRowIdentifier;
        requests.forEach((req) => onSelectionChange(req));
        return;
      }
      const selectRequest = selectOperation(
        selectionModel,
        fromRowKey,
        rangeSelect,
        keepExistingSelection,
        toRowKey,
      );

      lastActiveRef.current = newRowIdentifier;

      if (selectRequest) {
        onSelect?.(selectOperation === selectItem ? dataRow : null);
        onSelectionChange?.(selectRequest);
      }

      if (allRowsSelected && selectOperation === deselectItem) {
        setAllRowsSelected(false);
      }
    },
    [
      allRowsSelected,
      allowSelectCheckboxRow,
      isRowSelectable,
      onSelect,
      onSelectionChange,
      selectionModel,
    ],
  );

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLElement>>(
    (e) => {
      if (isSelectionEvent(e)) {
        const { current: rowIndex } = highlightedIndexRef;
        const { current: container } = containerRef;
        if (rowIndex !== undefined && rowIndex !== -1 && container) {
          const rowEl = getRowElementByAriaIndex(container, rowIndex);
          if (rowEl) {
            dispatchMouseEvent(rowEl, "click");
          }
        }
      }
    },
    [containerRef, highlightedIndexRef, isSelectionEvent],
  );

  const handleCheckboxColumnHeaderClick = useCallback(() => {
    setAllRowsSelected((allSelected) => {
      if (allSelected) {
        onSelectionChange({
          type: "DESELECT_ALL",
        });
      } else {
        onSelectionChange({
          type: "SELECT_ALL",
        });
      }
      return !allSelected;
    });
  }, [onSelectionChange]);

  return {
    allRowsSelected,
    onCheckBoxColumnHeaderClick: handleCheckboxColumnHeaderClick,
    onKeyDown: handleKeyDown,
    onRowClick: selectionAllowed ? handleRowClick : undefined,
  };
};
